import React, { useState, useEffect, useRef } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Snackbar,
} from "@mui/material";
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
} from "recharts";
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import html2canvas from 'html2canvas';
import apiService from "../../services/api";
import './ReportsList.css';

const downloadIcon = apiService.getBlobAssetUrl('download.svg');
const closeimage = apiService.getBlobAssetUrl('close_12.svg');
const sendIcon = apiService.getBlobAssetUrl('send_12.svg');
const SCORE_COLORS = {
  'Detractors (0.0–2.9)': '#219653',
  'Neutral (3.0–3.9)': '#F57921',
  'Promoters (4.0–5.0)': '#2F80ED',
};

const CSATDashboard = () => {
  const [insights, setInsights] = useState({});
  const [error, setError] = useState(null);
  const [showTableDownloadMenu, setShowTableDownloadMenu] = useState(false);
  const [showEmailMenu, setShowEmailMenu] = useState(false);
  const [email, setEmail] = useState('');
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const dashboardRef = useRef(null);

  useEffect(() => {
    const fetchInsights = async () => {
      try {
        const data = await apiService.fetchData("surveys/insights");
        setInsights(data);
      } catch (error) {
        console.error("Error fetching survey insights:", error);
        setError("Failed to load insights.");
      }
    };

    const loadUserInfo = async () => {
      try {
        const data = await apiService.fetchData('userslist');
        const results = data?.results || [];
        const formatted = results.map((item) => ({
          ID: item.ID || '',
          Name: item.Name || '',
          EmailID: item.EmailID || '',
        }));
        setUsers(formatted);
      } catch (error) {
        console.error('Error fetching users:', error);
        setSnackbarMessage('Failed to load users');
        setSnackbarOpen(true);
      }
    };

    fetchInsights();
    loadUserInfo();
  }, []);

  const monthlyData = (insights.monthly_averages?.results || []).map(d => ({
    ...d,
    MonthName: `${d.MonthName} ${d.Year}`,
    AverageCSATScore: parseFloat(d.AverageCSATScore),
  }));

  const scoreData = (insights.score_range_distribution?.results || []).map(d => ({
    ...d,
    Percentage: parseFloat(d.Percentage),
  }));

  const sectionData = (insights.average_by_section?.results || []).map(d => ({
    ...d,
    AverageCSATScore: parseFloat(d.AverageCSATScore),
  }));

  const scoreDistributionSummary = (insights.score_distribution_summary?.results || []).map(d => ({
    ...d,
    ResponseCount: parseInt(d.ResponseCount),
  }));

  const projectData = (insights.project_wise_csat?.results || []).map(d => ({
    ...d,
    AvgCSATScore: parseFloat(d.AvgCSATScore),
    LastSurveyDate: new Date(d.LastSurveyDate).toLocaleDateString(),
  }));

  const toggleTableDownloadMenu = () => setShowTableDownloadMenu(!showTableDownloadMenu);
  const toggleEmailMenu = () => setShowEmailMenu(!showEmailMenu);

const exportDashboardPDF = () => {
  const element = dashboardRef.current;
  if (!element) {
    console.error('Dashboard element not found in ref');
    setSnackbarMessage('Failed to capture dashboard for PDF');
    setSnackbarOpen(true);
    return;
  }

  const clonedElement = element.cloneNode(true);
  clonedElement.style.position = 'absolute';
  clonedElement.style.top = '0';
  clonedElement.style.left = '0';
  clonedElement.style.zIndex = '-1';
  clonedElement.style.background = '#fafafa';
  clonedElement.style.width = '100%';
  clonedElement.style.padding = '20px';
  clonedElement.style.overflow = 'visible';

  // Hide Download and Share buttons
  const downloadButtonTop = clonedElement.querySelector('#settings-download-wrapper2');
  const shareButton = clonedElement.querySelector('#settings-email-wrapper');
  const downloadButtonBottom = clonedElement.querySelector('#settings-download-wrapper1');
  if (downloadButtonTop) downloadButtonTop.style.display = 'none';
  if (shareButton) shareButton.style.display = 'none';
  if (downloadButtonBottom) downloadButtonBottom.style.display = 'none';

  document.body.appendChild(clonedElement);

  html2canvas(clonedElement, {
    useCORS: true,
    scale: 2,
    scrollX: 0,
    scrollY: 0,
    windowWidth: clonedElement.scrollWidth,
    windowHeight: clonedElement.scrollHeight,
  })
    .then((canvas) => {
      const imgData = canvas.toDataURL('image/jpeg', 0.8);
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const pdfWidth = imgWidth / 96 * 25.4; // Convert pixels to mm
      const pdfHeight = imgHeight / 96 * 25.4;

      const pdf = new jsPDF({
        orientation: pdfWidth > pdfHeight ? 'landscape' : 'portrait',
        unit: 'mm',
        format: [pdfWidth, pdfHeight],
        compress: true
      });

      // Add image with scaling to fit
      const ratio = pdf.internal.pageSize.getWidth() / imgWidth;
      const newHeight = imgHeight * ratio;
      pdf.addImage(imgData, 'JPEG', 0, 0, pdf.internal.pageSize.getWidth(), newHeight);

      // Handle pagination if content exceeds page height
      const pageHeight = pdf.internal.pageSize.getHeight();
      if (newHeight > pageHeight) {
        const totalPages = Math.ceil(newHeight / pageHeight);
        for (let i = 1; i < totalPages; i++) {
          pdf.addPage();
          pdf.addImage(imgData, 'JPEG', 0, -(pageHeight * i), pdf.internal.pageSize.getWidth(), newHeight);
        }
      }

      pdf.save('csat_dashboard.pdf');
      document.body.removeChild(clonedElement);
    })
    .catch((error) => {
      console.error('Error generating PDF:', error);
      setSnackbarMessage('Failed to export PDF');
      setSnackbarOpen(true);
      document.body.removeChild(clonedElement);
    });
};
  const exportTablePDF = () => {
    try {
      const doc = new jsPDF();
      doc.text('Project-wise CSAT Score', 10, 10);
      autoTable(doc, {
        startY: 20,
        head: [['Project Name', 'Avg CSAT', 'Promoter', 'Neutral', 'Detractor', 'Responses', 'Last Survey']],
        body: projectData.map((row) => [
          row.ProjectName,
          row.AvgCSATScore.toFixed(2),
          `${row.PromoterPercent}%`,
          `${row.NeutralPercent}%`,
          `${row.DetractorPercent}%`,
          row.ResponseCount,
          row.LastSurveyDate,
        ]),
      });
      const pdfBlob = doc.output('blob');
      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'project_csat.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting table PDF:', error);
      setSnackbarMessage('Failed to export table PDF');
      setSnackbarOpen(true);
    }
  };

  const exportTableToCSV = () => {
    try {
      const headers = [
        'Project Name',
        'Avg CSAT',
        'Promoter',
        'Neutral',
        'Detractor',
        'Responses',
        'Last Survey',
      ].join(',');

      const rows = projectData.map((row) =>
        [
          row.ProjectName,
          row.AvgCSATScore.toFixed(2),
          `${row.PromoterPercent}%`,
          `${row.NeutralPercent}%`,
          `${row.DetractorPercent}%`,
          row.ResponseCount,
          row.LastSurveyDate,
        ].join(',')
      );

      const csvContent = `${headers}\n${rows.join('\n')}`;
      const csvBlob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(csvBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'project_csat.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting table CSV:', error);
      setSnackbarMessage('Failed to export table CSV');
      setSnackbarOpen(true);
    }
  };

  const handleSendDashboardFile = async (fileType) => {
  if (!email) {
    setSnackbarMessage('Please enter an email address.');
    setSnackbarOpen(true);
    return;
  }

  const trimmedEmail = email.trim().toLowerCase();
  const element = dashboardRef.current;

  if (!element) {
    console.error('Dashboard element not found in ref');
    setSnackbarMessage('Failed to capture dashboard for email');
    setSnackbarOpen(true);
    return;
  }

  const emailButton = document.querySelector('#settings-email-wrapper');
  const emailDropdown = document.querySelector('#dashboard-email-dropdown-1');
  const originalEmailButtonStyle = emailButton ? emailButton.style.display : '';
  const originalEmailDropdownStyle = emailDropdown ? emailDropdown.style.display : '';

  if (emailButton) emailButton.style.display = 'none';
  if (emailDropdown) emailDropdown.style.display = 'none';

  const clonedElement = element.cloneNode(true);
  clonedElement.style.position = 'absolute';
  clonedElement.style.top = '0';
  clonedElement.style.left = '0';
  clonedElement.style.zIndex = '-1';
  clonedElement.style.background = '#fafafa';
  clonedElement.style.width = '100%';
  clonedElement.style.padding = '20px';
  clonedElement.style.overflow = 'visible';

  // Hide Download and Share buttons
  const downloadButtonTop = clonedElement.querySelector('#settings-download-wrapper2');
  const shareButton = clonedElement.querySelector('#settings-email-wrapper');
  const downloadButtonBottom = clonedElement.querySelector('#settings-download-wrapper1');
  if (downloadButtonTop) downloadButtonTop.style.display = 'none';
  if (shareButton) shareButton.style.display = 'none';
  if (downloadButtonBottom) downloadButtonBottom.style.display = 'none';

  document.body.appendChild(clonedElement);

  try {
    let attachment;
    let fileName;

    if (fileType === 'pdf') {
      const canvas = await html2canvas(clonedElement, {
        useCORS: true,
        scale: 1,
        scrollX: 0,
        scrollY: 0,
        windowWidth: clonedElement.scrollWidth,
        windowHeight: clonedElement.scrollHeight,
      });
      if (!canvas) {
        throw new Error('Canvas generation failed');
      }

      const imgData = canvas.toDataURL('image/jpeg', 0.5);
      const pdfWidth = clonedElement.scrollWidth / 96 * 25.4;
      const pdfHeight = clonedElement.scrollHeight / 96 * 25.4;

      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: [pdfWidth, pdfHeight],
        compress: true
      });
      pdf.addImage(imgData, 'JPEG', 0.1, 0.1, pdfWidth - 0.2, pdfHeight - 0.2);
      attachment = pdf.output('datauristring');
    }

    const emailData = {
      recipientName: trimmedEmail.split('@')[0] || 'Recipient',
      organizationName: 'CriticalRiver Technologies',
      dashboardTitle: 'Development Project Dashboard Report',
      to: trimmedEmail,
      subject: `Development Project Dashboard Report (${fileType.toUpperCase()})`,
      attachment: attachment,
    };

    const response = await apiService.sendDashboardEmail(
      emailData.recipientName,
      null,
      null,
      emailData.organizationName,
      emailData.dashboardTitle,
      emailData.to,
      emailData.subject,
      null,
      emailData.attachment
    );

    setSnackbarMessage(response.message || `Report sent successfully to ${trimmedEmail}!`);
    setSnackbarOpen(true);
    setEmail('');
    setShowEmailMenu(false);
  } catch (error) {
    console.error('Error sending dashboard email:', error);
    console.error('Full error details:', {
      message: error.message,
      response: error.response ? error.response.data : 'No response',
      status: error.response ? error.response.status : 'Unknown',
    });
    if (error.message.includes('554') || error.message.includes('MaxSubmissionExceeded')) {
      setSnackbarMessage('Email failed: Attachment size exceeds server limit. Please download the file manually.');
    } else {
      setSnackbarMessage(error.response?.data?.error || error.message || 'Failed to send dashboard email');
    }
    setSnackbarOpen(true);
  } finally {
    if (emailButton) emailButton.style.display = originalEmailButtonStyle;
    if (emailDropdown) emailDropdown.style.display = originalEmailDropdownStyle;

    if (clonedElement.parentNode === document.body) {
      document.body.removeChild(clonedElement);
    }
  }
};
  const handleClearInput = () => {
    setEmail('');
    setSuggestions([]);
  };

  const handleEmailInputChange = (e) => {
    const input = e.target.value;
    setEmail(input);

    if (input.trim() === '') {
      setSuggestions([]);
      return;
    }

    const filteredSuggestions = users.filter(
      (user) =>
        user?.EmailID?.toLowerCase().includes(input.toLowerCase()) ||
        user?.Name?.toLowerCase().includes(input.toLowerCase())
    );

    setSuggestions(filteredSuggestions);
  };

  const handleSuggestionClick = (user) => {
    setEmail(user.EmailID);
    setSuggestions([]);
  };

  const renderSuggestionText = (text, searchTerm) => {
    if (!searchTerm) return <span>{text}</span>;

    const lowerText = text?.toLowerCase() ?? '';
    const lowerSearchTerm = searchTerm.toLowerCase();
    const index = lowerText.indexOf(lowerSearchTerm);

    if (index === -1) return <span>{text}</span>;

    const beforeMatch = text.substring(0, index);
    const match = text.substring(index, index + searchTerm.length);
    const afterMatch = text.substring(index + searchTerm.length);

    return (
      <>
        <span className="suggestion-non-match">{beforeMatch}</span>
        <span className="suggestion-match">{match}</span>
        <span className="suggestion-non-match">{afterMatch}</span>
      </>
    );
  };

  return (
    <section>
      <div className="dashboard" ref={dashboardRef}>
        {error && <p style={{ color: 'red' }}>{error}</p>}
        <div className="resource-dashboard-actions-right" style={{ position: 'relative', marginBottom: '15px' }}>
          <button
            className="icon-btn"
            id="settings-download-wrapper2"
            onClick={exportDashboardPDF}
            aria-label="Download entire dashboard as PDF"
          >
            <img src={downloadIcon} alt="Download Entire Dashboard" className="action-icon" />
            <span>Download</span>
          </button>
          <button
            className="icon-btn"
            id="settings-email-wrapper"
            onClick={toggleEmailMenu}
            aria-label="Toggle email options for entire dashboard"
          >
            <img src={sendIcon} alt="Send" className="action-icon" />
            <span>Share</span>
          </button>
          {showEmailMenu && (
            <div className="dashboard-email-dropdown" id="dashboard-email-dropdown-1">
              <div className="email-input-wrapper">
                <input
                  type="email"
                  value={email}
                  aria-label="Enter email address"
                  onChange={handleEmailInputChange}
                  placeholder="Enter email address"
                  style={{ padding: '5px', marginBottom: '5px', width: '100%', minWidth: '180px' }}
                />
                {email && (
                  <button
                    type="button"
                    className="clear-img11"
                    id="clear-img11-1"
                    onClick={handleClearInput}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        handleClearInput();
                      }
                    }}
                    aria-label="Clear email input"
                    tabIndex={0}
                  >
                    <img src={closeimage} alt="clear" />
                  </button>
                )}
                {suggestions.length > 0 && (
                  <ul className="suggestions-dropdown" id="suggestions-dropdown-1">
                    {suggestions.map((user, index) => (
                      <li
                        key={user.ID}
                        onClick={() => handleSuggestionClick(user)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            handleSuggestionClick(user);
                          }
                        }}
                        className={`suggestion-item ${index % 2 === 0 ? 'even' : 'odd'}`}
                        tabIndex={0}
                      >
                        <div>{renderSuggestionText(user.Name, email)}</div>
                        <div>{renderSuggestionText(user.EmailID, email)}</div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="dashboard-export-option" onClick={() => handleSendDashboardFile('pdf')}>
                Send PDF
              </div>
            </div>
          )}
        </div>
        <div className="ams-dashboard-wrapper">
           <div className="ams-dashboard">

          <div className="dashboard-section">
              <div className="cards-container">
                {[
                  { label: 'Overall CSAT', value: parseFloat(insights.average_rating?.results?.[0]?.AverageRating || 0).toFixed(2), className: 'orange' },
                  { label: '% Promoters', value: `${insights.promoters?.results?.[0]?.Promoters || 0} %`, className: 'green' },
                  { label: '% Neutral', value: `${insights.neutral?.results?.[0]?.Neutral || 0} %`, className: 'blue' },
                  { label: '% Detractors', value: `${insights.detractors?.results?.[0]?.Detractors || 0} %`, className: 'darkblue' },
                  { label: 'Response Count', value: insights.total_responses?.results?.[0]?.Responses || 0, className: 'purple' },
                ].map((card) => (
                  <div key={card.label} className={`ams-card ${card.className}`} style={{ boxShadow: 'none', border: 'none' }}>
                    <span className="ams-card-title">{card.label}</span>
                    <span className="ams-card-value">{card.value}</span>
                  </div>
                ))}
              </div>

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px', marginBottom: '20px' }}>
                <div className="resource-chart-box pie-chart" style={{ padding: '15px',width:'50% !important', background: '#fff' }}>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-600" id="chart1">CSAT Score Trend</h3>
                    {monthlyData.length ? (
                      <ResponsiveContainer id="chart9" width="100%" height={220}>
                        <LineChart data={monthlyData}>
                          <XAxis dataKey="MonthName" stroke="#666" />
                          <YAxis domain={[3, 5]} stroke="#666" label={{
                            value: "Average Score",
                            angle: -90,
                            position: "insideLeft",
                            offset: 10,
                            style: {
                              textAnchor: "middle",
                              fill: "#666",
                              fontSize: 12,
                            },
                          }} />
                          <Tooltip />
                          <Legend />
                          <Line type="monotone" dataKey="AverageCSATScore" stroke="#FF7300" strokeWidth={2} dot={{ r: 4 }} />
                        </LineChart>
                      </ResponsiveContainer>
                    ) : <p>No data available</p>}
                  </div>
                </div>

                <div className="resource-chart-box pie-chart" style={{ padding: '15px',width:'50% !important', background: '#fff' }}>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-600" id="chart1">Score Range Distribution</h3>
                    {scoreData.length ? (
                      <ResponsiveContainer id="chart9" width="100%" height={220}>
                        <BarChart data={scoreData}>
                          <XAxis dataKey="ScoreRange" stroke="#666" />
                          <YAxis stroke="#666" label={{
                            value: "Response Count",
                            angle: -90,
                            position: "insideLeft",
                            offset: 10,
                            style: {
                              textAnchor: "middle",
                              fill: "#666",
                              fontSize: 12,
                            },
                          }}/>
                          <Tooltip />
                          <Legend />
                          <Bar dataKey="ResponseCount" fill="#3498db" />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : <p>No data available</p>}
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', width:'50% !important', flexWrap: 'wrap', gap: '15px', marginBottom: '20px' }}>
                <div className="resource-chart-box pie-chart" style={{ padding: '15px', background: '#fff' }}>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-600" id="chart1">Score Distribution</h3>
                    {scoreDistributionSummary.length ? (
                      <ResponsiveContainer id="chart8" width="100%" height={220}>
                        <PieChart>
                          <Pie
                            id="chart4"
                            data={scoreDistributionSummary}
                            dataKey="ResponseCount"
                            nameKey="ScoreRange"
                            cx="50%"
                            cy="50%"
                            outerRadius={65}
                            labelLine={false}
                            label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                          >
                            {scoreDistributionSummary.map((entry) => (
                              <Cell key={entry.ScoreRange} fill={SCORE_COLORS[entry.ScoreRange] || '#ccc'} />
                            ))}
                          </Pie>
                          <Tooltip />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : <p>No data available</p>}
                  </div>
                </div>

                <div className="resource-chart-box pie-chart" style={{ padding: '15px', background: '#fff' }}>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-600" id="chart1">Average Rating per Section</h3>
                    {sectionData.length ? (
                      <ResponsiveContainer id="chart9" width="100%" height={220}>
                        <BarChart layout="vertical" data={sectionData} margin={{ top: 0, right: 5, left: 15, bottom: 5 }}>
                          <XAxis type="number" domain={[0, 5]} ticks={[0, 1, 2, 3, 4, 5]} stroke="#666" />
                          <YAxis dataKey="SectionName" type="category" width={100} />
                          <Tooltip />
                          <Legend />
                          <Bar dataKey="AverageCSATScore" fill="#FF7300" />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : <p>No data available</p>}
                  </div>
                </div>
              </div>

              <div style={{ boxShadow: 'none', border: 'none', padding: 0, width: '100%' }}>
                <div id="results4">
                  <h3 className="text-lg font-semibold text-gray-600 mb-3" id="chart1">Project-wise CSAT Score</h3>
                  <div className="settings-download-wrapper" id="settings-download-wrapper1">
                    <button
                      className="icon-btn"
                      onClick={toggleTableDownloadMenu}
                      aria-label="Toggle download options"
                    >
                      <img src={downloadIcon} alt="Download" className="action-icon"/>
                      <span>Download</span>
                    </button>
                    {showTableDownloadMenu && (
                      <div className="Project-export-dropdown">
                        <button
                          id="Project-export-option11"
                          className="Project-export-option"
                          onClick={() => {
                            exportTableToCSV();
                            toggleTableDownloadMenu();
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              exportTableToCSV();
                              toggleTableDownloadMenu();
                            }
                          }}
                        >
                          Download CSV
                        </button>
                        <button
                          id="Project-export-option11"
                          className="Project-export-option"
                          onClick={() => {
                            exportTablePDF();
                            toggleTableDownloadMenu();
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              exportTablePDF();
                              toggleTableDownloadMenu();
                            }
                          }}
                        >
                          Download PDF
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                {projectData.length ? (
                  <TableContainer component={Paper} style={{ boxShadow: 'none',textAlign:'center' }}>
                    <Table className="resource-csat-table1">
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Project Name</strong></TableCell>
                          <TableCell><strong>Avg CSAT</strong></TableCell>
                          <TableCell><strong>Promoter</strong></TableCell>
                          <TableCell><strong>Neutral</strong></TableCell>
                          <TableCell><strong>Detractor</strong></TableCell>
                          <TableCell><strong>Responses</strong></TableCell>
                          <TableCell><strong>Last Survey</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {projectData.map((project) => (
                          <TableRow key={project.ProjectName}>
                            <TableCell>{project.ProjectName}</TableCell>
                            <TableCell>{project.AvgCSATScore.toFixed(2)}</TableCell>
                            <TableCell>{project.PromoterPercent}%</TableCell>
                            <TableCell>{project.NeutralPercent}%</TableCell>
                            <TableCell>{project.DetractorPercent}%</TableCell>
                            <TableCell>{project.ResponseCount}</TableCell>
                            <TableCell>{project.LastSurveyDate}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : <p>No data available</p>}
              </div>
            </div>
          </div>
        </div>
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={5000}
          onClose={() => setSnackbarOpen(false)}
          message={snackbarMessage}
          sx={{ '& .MuiSnackbarContent-root': { backgroundColor: snackbarMessage.includes('successful') ? '#4caf50' : '#f44336' } }}
        />
      </div>
    </section>
  );
};

export default CSATDashboard;